import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import confetti from "canvas-confetti";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Music, Users, Sparkles, ShoppingCart, Trash2, Brain, MapPin, DollarSign, Calendar } from "lucide-react";

import { TieredPackage, calculateSmartPricing, PricingAdjustments } from "@/lib/packageTiers";
import { generateEnsemblePackages, EnsemblePackage } from "@/lib/aiPackageGenerator";
import { useToast } from "@/hooks/use-toast";
import { CartIncentives } from "@/components/CartIncentives";
import { motion } from "framer-motion";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { PostInquiryAuthDialog } from "@/components/PostInquiryAuthDialog";
import { CreatePasswordSplash } from "@/components/CreatePasswordSplash";
import { ConsultationScheduler, ScheduledCall } from "@/components/ConsultationScheduler";

// Cultural analysis is now done server-side during inquiry submission

interface RecommendedPackage extends EnsemblePackage {
  eventIndex: number;
  eventName: string;
  matchScore: number;
  genreAlignment: string[];
  culturalMatch?: boolean;
  instrumentMatch?: number;
  smartPricing?: PricingAdjustments;
  adjustedPrice?: number;
  recommendedInstruments?: string[];
  tier?: string;
  tierBadge?: string | null;
  bestValue?: boolean;
}

interface CartItem {
  package: RecommendedPackage;
  customized: boolean;
  swappedMusicians?: Map<string, string>;
  eventDate?: string;
  isConsecutive?: boolean;
}

interface BundleDiscount {
  itemIndices: number[];
  discountPercentage: number;
  savingsAmount: number;
  bundleType: 'consecutive' | 'same-event' | 'multi-moment';
  description: string;
}



const Shop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [eventRecommendations, setEventRecommendations] = useState<Map<number, RecommendedPackage[]>>(new Map());
  // Cultural analysis is now done server-side during submission only
  const [sessionId] = useState(() => sessionStorage.getItem('session_id') || '');
  const [playlistData, setPlaylistData] = useState<any>(null);
  const [venueCoordinates, setVenueCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [guestCount, setGuestCount] = useState<number>(100);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [bundleDiscounts, setBundleDiscounts] = useState<BundleDiscount[]>([]);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showPasswordSplash, setShowPasswordSplash] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [scheduledCall, setScheduledCall] = useState<ScheduledCall | null>(null);
  const { contactData, formData, initialEventIndex } = location.state || {};

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsUserLoggedIn(!!session?.user);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsUserLoggedIn(!!session?.user);
    });

    return () => subscription.unsubscribe();
  }, []);


  // Session tracking
  const { trackInteraction, trackCartUpdate, saveAnalytics } = useSessionTracking({
    sessionId,
    onSave: () => console.log('Session analytics saved'),
  });

  useEffect(() => {
    if (!sessionId) {
      toast({
        title: "Session not found",
        description: "Please start from the beginning",
        variant: "destructive",
      });
      navigate('/');
      return;
    }

    loadPlaylistDataAndRecommendPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadPlaylistDataAndRecommendPackages = async () => {
    try {
      console.log('Starting to load shop data...');
      setLoading(true);

      // Load favorite songs from database
      console.log('Loading favorite songs for session:', sessionId);
      const { data: songs, error: songsError } = await supabase
        .from('favorite_songs')
        .select('*')
        .eq('session_id', sessionId)
        .order('event_index', { ascending: true });

      console.log('Loaded songs:', songs?.length || 0, 'songs');

      if (songsError) {
        console.error('Error loading songs:', songsError);
        toast({
          title: "Error loading data",
          description: "Could not load your selections",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (!songs || songs.length === 0) {
        console.error('No songs found for session');
        toast({
          title: "No selections found",
          description: "Please add some songs to your taste profile first",
          variant: "destructive",
        });
        setLoading(false);
        navigate('/vibe-builder');
        return;
      }

      // Group songs by event
      const eventGroups = songs.reduce((acc: any, song: any) => {
        const eventIdx = song.event_index || 0;
        if (!acc[eventIdx]) acc[eventIdx] = [];
        acc[eventIdx].push(song);
        return acc;
      }, {});

      console.log('🎵 Songs grouped by event_index:', Object.keys(eventGroups).map(k => ({
        eventIndex: k,
        songCount: eventGroups[k].length
      })));

      // Load saved event steps from contact form drafts to know the actual number of events
      const { data: savedDraft } = await supabase
        .from('contact_form_drafts')
        .select('form_data')
        .eq('session_id', sessionId)
        .maybeSingle();

      const savedEventSteps = (savedDraft?.form_data as any)?.eventSteps;
      const actualEventCount = savedEventSteps?.length || 1;

      console.log('📋 Actual events from vibe builder:', actualEventCount);
      console.log('📦 Event groups found in songs:', Object.keys(eventGroups).length);

      // Only create eventSteps for events that actually exist (filter out any erroneous event_index values)
      const eventSteps = Object.keys(eventGroups)
        .map(idx => parseInt(idx))
        .filter(eventIndex => eventIndex < actualEventCount) // Only include valid event indices
        .map((eventIndex) => {
          const eventSongs = eventGroups[eventIndex];
          const savedStep = savedEventSteps?.[eventIndex];
          return {
            eventIndex,
            occasion: savedStep?.occasion || eventSongs[0]?.occasion || 'Event',
            vibe: savedStep?.vibe || eventSongs[0]?.vibe || '',
            vibes: savedStep?.vibes || eventSongs[0]?.vibes || [],
            selectedGenres: savedStep?.selectedGenres || [],
            selectedVibes: savedStep?.selectedVibes || [],
            songs: eventSongs
          };
        });

      console.log('✅ Final eventSteps to show packages for:', eventSteps.length);

      setPlaylistData({ eventSteps });

      // Get venue coordinates if available (non-blocking)
      if (contactData?.venueAddress) {
        supabase.functions.invoke('geocode-venue', {
          body: { address: contactData.venueAddress }
        }).then(({ data, error }) => {
          if (!error && data?.coordinates) {
            setVenueCoordinates(data.coordinates);
          }
        }).catch(() => {
          // Silently fail - not critical
        });
      }

      // Set guest count
      if (contactData?.guestCount) {
        setGuestCount(contactData.guestCount);
      }

      // Generate packages immediately with basic data
      generatePackagesForEvents(eventSteps);

      // Cultural analysis is now done server-side during inquiry submission only

      setLoading(false);

    } catch (error) {
      console.error('Error in loadPlaylistDataAndRecommendPackages:', error);
      toast({
        title: "Error",
        description: "Something went wrong loading the shop",
        variant: "destructive",
      });
      setLoading(false);
    }
  };


  // Generate basic packages without waiting for cultural analysis
  const generatePackagesForEvents = (eventSteps: any[]) => {
    const recommendationsMap = new Map<number, RecommendedPackage[]>();

    eventSteps.forEach((event, eventIndex) => {
      const primaryMoment = 'Cocktail Hour';
      const ensemblePackages = generateEnsemblePackages(
        primaryMoment,
        [],
        'Mixed'
      );

      const recommendations: RecommendedPackage[] = ensemblePackages.map((pkg, index) => {
        let matchScore = 100 - (pkg.ensembleSize * 2);
        
        const pricingAdjustments = calculateSmartPricing(
          pkg.basePrice,
          venueCoordinates?.[0] || 0,
          venueCoordinates?.[1] || 0,
          guestCount,
          pkg.guestCountMin,
          pkg.guestCountMax
        );

        return {
          ...pkg,
          eventIndex,
          eventName: event.occasion || `Event ${eventIndex + 1}`,
          matchScore,
          genreAlignment: [],
          smartPricing: pricingAdjustments,
          adjustedPrice: pricingAdjustments.finalPrice,
          bestValue: index === 2,
        };
      });

      recommendationsMap.set(eventIndex, recommendations);
    });

    setEventRecommendations(recommendationsMap);
  };

  const addToCart = (pkg: RecommendedPackage) => {
    const existingEventPackageIndex = cart.findIndex(item => item.package.eventIndex === pkg.eventIndex);

    const runTracking = (updatedCart: CartItem[], action: 'package_switched' | 'add_to_cart', extra?: any) => {
      try {
        trackInteraction('click', 'ensemble_package', {
          ensembleSize: pkg.ensembleSize,
          packageName: pkg.name,
          price: pkg.adjustedPrice || pkg.basePrice,
          eventIndex: pkg.eventIndex,
        });

        void trackCartUpdate(updatedCart.map(item => ({
          packageName: item.package.name,
          eventIndex: item.package.eventIndex,
          eventName: item.package.eventName,
          price: item.package.adjustedPrice || item.package.basePrice,
          customized: item.customized,
          musicians: item.package.musicians,
          moment: item.package.moment,
          ensembleSize: item.package.ensembleSize,
        }))).catch((err) => {
          console.error('Error tracking cart update:', err);
        });

        trackInteraction('cart_action', action, extra || { packageName: pkg.name });
      } catch (err) {
        console.error('Error in analytics tracking:', err);
      }
    };

    // If package already exists for this event, replace it
    if (existingEventPackageIndex >= 0) {
      const existingPackage = cart[existingEventPackageIndex].package;
      const newCart = [...cart];
      newCart[existingEventPackageIndex] = { package: pkg, customized: false };
      setCart(newCart);
      calculateBundleDiscounts(newCart);

      runTracking(newCart, 'package_switched', { from: existingPackage.name, to: pkg.name });

      // Auto-open cart after switching a package
      setCartOpen(true);
      return;
    }

    // Add new package
    const newCart = [...cart, { package: pkg, customized: false }];
    setCart(newCart);
    calculateBundleDiscounts(newCart);

    runTracking(newCart, 'add_to_cart');

    toast({
      title: "Added to cart",
      description: `${pkg.name} for ${pkg.eventName}`,
    });

    // Auto-open cart only when ALL events have packages selected
    const totalEvents = eventRecommendations.size;
    const eventsWithPackagesNow = new Set(newCart.map(item => item.package.eventIndex)).size;
    if (eventsWithPackagesNow >= totalEvents && totalEvents > 0) {
      setCartOpen(true);
    }
  };
  const removeFromCart = (index: number) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    calculateBundleDiscounts(newCart);
    
    // If cart is now empty, close the cart sidebar
    if (newCart.length === 0) {
      setCartOpen(false);
    }
    
    try {
      void trackCartUpdate(newCart.map(item => ({
        packageName: item.package.name,
        eventIndex: item.package.eventIndex,
        eventName: item.package.eventName,
        price: item.package.adjustedPrice || item.package.basePrice,
        customized: item.customized,
        musicians: item.package.musicians,
        moment: item.package.moment,
        ensembleSize: item.package.ensembleSize,
      }))).catch((err) => {
        console.error('Error tracking cart update on remove:', err);
      });

      trackInteraction('cart_action', 'remove_from_cart');
    } catch (err) {
      console.error('Error in analytics tracking on remove:', err);
    }
    
    toast({
      title: "Removed from cart",
      description: newCart.length === 0 ? "Cart is empty" : "Package removed successfully",
    });
  };
  // Calculate bundle discounts for multi-date/multi-moment packages
  const calculateBundleDiscounts = (cartItems: CartItem[]) => {
    const discounts: BundleDiscount[] = [];
    
    // Group items by event
    const eventGroups = new Map<number, number[]>();
    cartItems.forEach((item, index) => {
      const eventIndex = item.package.eventIndex;
      if (!eventGroups.has(eventIndex)) {
        eventGroups.set(eventIndex, []);
      }
      eventGroups.get(eventIndex)!.push(index);
    });

    // Check for multi-moment bundles (multiple packages for same event)
    eventGroups.forEach((indices, eventIndex) => {
      if (indices.length >= 2) {
        const packages = indices.map(i => cartItems[i].package);
        const totalPrice = packages.reduce((sum, pkg) => sum + (pkg.adjustedPrice || pkg.basePrice), 0);
        
        // 10% discount for 2-3 moments, 15% for 4+ moments
        const discountPercentage = indices.length >= 4 ? 0.15 : 0.10;
        const savingsAmount = Math.ceil(totalPrice * discountPercentage);
        
        discounts.push({
          itemIndices: indices,
          discountPercentage: discountPercentage * 100,
          savingsAmount,
          bundleType: 'multi-moment',
          description: `${indices.length} moments for ${packages[0].eventName}`
        });
      }
    });

    // Check for consecutive event dates (if we have multiple events)
    if (eventGroups.size >= 2) {
      const allIndices = Array.from({ length: cartItems.length }, (_, i) => i);
      const totalPrice = cartItems.reduce((sum, item) => sum + (item.package.adjustedPrice || item.package.basePrice), 0);
      
      // 12% discount for multi-event bookings
      const discountPercentage = 0.12;
      const savingsAmount = Math.ceil(totalPrice * discountPercentage);
      
      discounts.push({
        itemIndices: allIndices,
        discountPercentage: discountPercentage * 100,
        savingsAmount,
        bundleType: 'consecutive',
        description: `${eventGroups.size} events booked together`
      });
    }

    setBundleDiscounts(discounts);
  };

  const calculateTotal = (): number => {
    const subtotal = cart.reduce((total, item) => {
      return total + (item.package.adjustedPrice || item.package.basePrice);
    }, 0);

    // Apply largest bundle discount
    const maxBundleDiscount = bundleDiscounts.reduce((max, discount) => 
      Math.max(max, discount.savingsAmount), 0
    );

    return Math.max(0, subtotal - maxBundleDiscount);
  };

  const getTotalSavings = (): number => {
    const bundleSavings = bundleDiscounts.reduce((max, discount) => 
      Math.max(max, discount.savingsAmount), 0
    );

    return bundleSavings;
  };

  const handleBackNavigation = () => {
    // If we have contact data, send the user back into the taste profile flow
    if (contactData && formData) {
      const eventIndices = Array.from(eventRecommendations.keys());
      const lastEventIndex = eventIndices.length
        ? Math.max(...eventIndices)
        : (typeof initialEventIndex === 'number' ? initialEventIndex : 0);

      navigate('/vibe-builder', {
        state: {
          contactData,
          formData,
          initialEventIndex: lastEventIndex,
        },
      });
      return;
    }

    // Fallback: go back in history
    navigate(-1);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast({
        title: "Cart is empty",
        description: "Please add packages to continue",
        variant: "destructive",
      });
      return;
    }

    // Validate contact data exists
    if (!contactData?.name || !contactData?.email || !contactData?.phone) {
      toast({
        title: "Missing contact information",
        description: "Please start from the beginning to provide your contact details",
        variant: "destructive",
      });
      navigate('/');
      return;
    }

    // 🎉 Celebration for clicking Submit!
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6'],
    });

    try {
      console.log('🚀 Starting inquiry submission...');
      console.log('📦 Cart items:', cart.length);
      console.log('🎵 Session ID:', sessionId);

      // Show loading toast
      toast({
        title: "Submitting inquiry...",
        description: "Sending your event details",
      });

      // Load songs from database for this session
      console.log('🎶 Fetching songs from database...');
      const { data: songs, error: songsError } = await supabase
        .from('favorite_songs')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (songsError) {
        console.warn('⚠️ Error fetching songs for inquiry, continuing without songs:', songsError);
      }

      const safeSongs = (songs || []).map(song => ({
        song_title: song.song_title,
        artist: song.artist,
        album: song.album,
        spotify_uri: song.spotify_uri,
        event_index: song.event_index,
      }));
      console.log('✅ Loaded songs for inquiry (slimmed payload):', safeSongs.length);

      // Extract event information from playlistData and formData - include full event details per event
      const eventSteps = playlistData?.eventSteps || [];
      
      // Load saved form data from contact form drafts for complete event details (dates, times, venues)
      const { data: savedDraft } = await supabase
        .from('contact_form_drafts')
        .select('form_data')
        .eq('session_id', sessionId)
        .maybeSingle();
      
      const savedFormData = savedDraft?.form_data as any;
      const savedEventSteps = savedFormData?.eventSteps || [];
      
      // Build comprehensive per-event data merging playlistData (vibes/genres) with savedFormData (dates/times/venues)
      const eventDates = eventSteps.length > 0 
        ? eventSteps.map((step: any, idx: number) => {
            // Get the saved form step for this event (has dates, times, venue, guest count)
            const savedStep = savedEventSteps[idx] || {};
            
            return {
              // Dates and times from saved form data
              date: savedStep.date ? new Date(savedStep.date).toLocaleDateString() : undefined,
              startTime: savedStep.startTime || savedStep.event_start_time,
              endTime: savedStep.endTime || savedStep.event_end_time,
              loadInTime: savedStep.loadInTime || savedStep.load_in_time,
              // Venue and guest info from saved form data
              venueAddress: savedStep.venueAddress || savedStep.venue_address,
              guestCount: savedStep.guestCount || savedStep.guest_count,
              // Occasion and vibes/genres from playlist step
              occasion: step.occasion || savedStep.occasion,
              vibes: step.vibes || step.selectedVibes || savedStep.vibes || (step.vibe ? [step.vibe] : []),
              genres: step.selectedGenres || savedStep.selectedGenres || [],
            };
          })
        : (contactData?.event_date ? [{
            date: contactData.event_date,
            startTime: contactData.event_start_time,
            endTime: contactData.event_end_time,
            loadInTime: contactData.load_in_time,
            venueAddress: contactData.venue_address || contactData.venueAddress,
            guestCount: contactData.guest_count || contactData.guestCount,
          }] : []);
      
      console.log('📅 Event dates built for email:', eventDates);

      // Check for imported playlists in session storage
      let importedPlaylists: any[] = [];
      try {
        const spotifyData = sessionStorage.getItem(`spotify_import_${sessionId}`);
        if (spotifyData) {
          const parsed = JSON.parse(spotifyData);
          if (parsed.playlists) {
            importedPlaylists = parsed.playlists.map((p: any) => ({
              name: p.name,
              url: p.external_urls?.spotify || p.url,
              trackCount: p.tracks?.total || p.trackCount || 0,
            }));
          }
        }
      } catch (e) {
        console.warn('Could not parse imported playlists:', e);
      }

      // Prepare inquiry data with comprehensive per-event information
      const inquiryData = {
        packages: cart.map(item => ({
          name: item.package.name,
          price: item.package.adjustedPrice || item.package.basePrice,
          musicians: item.package.musicians.join(', '),
          description: item.package.description,
          eventName: item.package.eventName,
          eventIndex: item.package.eventIndex,
          moment: item.package.moment,
          instrumentMatch: item.package.instrumentMatch,
          culturalMatch: item.package.culturalMatch,
        })),
        songs: safeSongs,
        // Cultural analysis is now performed server-side during submission
        culturalAnalysis: [],
        sessionId,
        contactInfo: contactData,
        totalCost: calculateTotal(),
        totalSavings: getTotalSavings(),
        // Include scheduled call data for calendar invite in email
        scheduledCall: scheduledCall ? {
          utcDateTime: scheduledCall.utcDateTime,
          timezone: scheduledCall.timezone,
        } : undefined,
        eventInfo: {
          dates: eventDates, // Each date now has its own vibes and genres
          importedPlaylists: importedPlaylists.length > 0 ? importedPlaylists : undefined,
        },
      };

      console.log('📧 Sending email with inquiry data...');
      console.log('📊 Data summary:', {
        packages: inquiryData.packages.length,
        songs: inquiryData.songs.length,
        totalCost: inquiryData.totalCost,
      });

      // Save submission to database first
      console.log('💾 Saving submission to database...');
      
      const submissionData = {
        name: contactData?.name || '',
        email: contactData?.email || '',
        phone: contactData?.phone || '',
        venueAddress: contactData?.venueAddress || (contactData as any)?.venue_address || '',
        eventDate: eventDates[0]?.date || (contactData as any)?.event_date || new Date().toISOString(),
        eventStartTime: eventDates[0]?.startTime || (contactData as any)?.event_start_time || '18:00',
        eventEndTime: eventDates[0]?.endTime || (contactData as any)?.event_end_time || '23:00',
        loadInTime: eventDates[0]?.loadInTime || (contactData as any)?.load_in_time || null,
        userTimezone: scheduledCall?.timezone || (contactData as any)?.user_timezone || 'America/Chicago',
        eventDatesJson: eventDates.length > 0 ? eventDates : null,
        notes: `Packages: ${cart.map(item => item.package.name).join(', ')}. Total: $${calculateTotal().toLocaleString()}`,
        // Complete inquiry data for database storage
        sessionId,
        totalCost: calculateTotal(),
        totalSavings: getTotalSavings(),
        packages: inquiryData.packages,
        culturalAnalysis: inquiryData.culturalAnalysis,
        songs: inquiryData.songs,
        occasion: eventDates[0]?.occasion || (contactData as any)?.occasion || null,
        vibes: eventDates.flatMap(e => e.vibes || []),
        // Scheduled call at submission time
        scheduledCallAt: scheduledCall?.utcDateTime || null,
      };

      // Get current session to pass auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data: submissionResponse, error: submissionError } = await supabase.functions.invoke('submit-contact', {
        body: submissionData,
        headers: session?.access_token ? {
          Authorization: `Bearer ${session.access_token}`,
        } : undefined,
      });

      if (submissionError) {
        console.error('❌ Failed to save submission:', submissionError);
        throw new Error(`Failed to save submission: ${submissionError.message || JSON.stringify(submissionError)}`);
      }

      console.log('✅ Submission saved to database:', submissionResponse);
      
      // Store submission ID for scheduling call
      const newSubmissionId = submissionResponse?.data?.id;
      if (newSubmissionId) {
        setSubmissionId(newSubmissionId);
      }

      // Call edge function to send email
      console.log('🔗 Invoking send-inquiry-email edge function...');
      const { data: emailResponse, error: emailError } = await supabase.functions.invoke('send-inquiry-email', {
        body: inquiryData,
      });

      console.log('📬 Edge function response:', { emailResponse, emailError });

      if (emailError) {
        console.error('❌ Email function error:', emailError);
        console.error('❌ Full error details:', JSON.stringify(emailError, null, 2));
        throw new Error(`Failed to send email: ${emailError.message || JSON.stringify(emailError)}`);
      }

      if (!emailResponse || emailResponse.error) {
        console.error('❌ Email response contains error:', emailResponse);
        throw new Error(`Email send failed: ${emailResponse?.error || 'Unknown error'}`);
      }

      console.log('✅ Email sent successfully:', emailResponse);

      // 🎉 BIG celebration for successful submission!
      confetti({
        particleCount: 150,
        spread: 180,
        origin: { y: 0.6 },
        colors: ['#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6'],
        startVelocity: 45,
        gravity: 1,
        scalar: 1.5,
      });
      
      setTimeout(() => {
        confetti({
          particleCount: 100,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: ['#ec4899', '#f472b6', '#fbcfe8'],
        });
        confetti({
          particleCount: 100,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: ['#8b5cf6', '#a78bfa', '#c4b5fd'],
        });
      }, 250);

      // Clear inquiry in progress flag - submission is complete
      sessionStorage.removeItem('inquiry_in_progress');

      // Show success toast
      toast({
        title: "Consultation booked successfully! 📞",
        description: "Check your email for confirmation and calendar invite.",
      });

      // Close the cart sidebar since we're moving to auth flow
      setCartOpen(false);
      
      // After successful submission, proceed to auth dialog or dashboard
      if (!isUserLoggedIn) {
        setTimeout(() => {
          setShowAuthDialog(true);
        }, 1500);
      } else {
        setTimeout(() => {
          setShowPasswordSplash(true);
        }, 1500);
      }

    } catch (error: any) {
      console.error('❌ Critical error submitting inquiry:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
      });

      const rawMessage = String(error?.message || "");
      const friendlyMessage = /edge function not found|functionshttperror|\b404\b/i.test(rawMessage)
        ? "Our submission service isn’t reachable on this site build. If this only happens on the live domain, publish the latest version and hard-refresh."
        : (rawMessage || "Please try again or contact us directly");

      toast({
        title: "Submission failed",
        description: friendlyMessage,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Curating perfect packages for your events...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PostInquiryAuthDialog
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
        email={contactData?.email || ""}
        name={contactData?.name}
        phone={contactData?.phone}
      />
      

      <CreatePasswordSplash
        open={showPasswordSplash}
        onComplete={() => {
          setShowPasswordSplash(false);
          navigate('/dashboard', { replace: true });
        }}
        userName={contactData?.name}
      />
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
          <div className="container max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBackNavigation}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <h1 className="text-2xl font-bold">Custom Package Shop</h1>
            
            {/* Cart Button */}
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="gap-2 relative">
                  <ShoppingCart className="h-4 w-4" />
                  Cart
                  {cart.length > 0 && (
                    <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                      {cart.length}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Shopping Cart</SheetTitle>
                  <SheetDescription>
                    {cart.length} package(s) selected
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-8 space-y-4">
                  {cart.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Your cart is empty</p>
                    </div>
                  ) : (
                    <>
                      {cart.map((item, index) => (
                        <Card key={index} className="mb-3">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <CardTitle className="text-base">{item.package.name}</CardTitle>
                                  {bundleDiscounts.some(d => d.itemIndices.includes(index)) && (
                                    <Badge variant="default" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white animate-pulse">
                                      Bundle Savings!
                                    </Badge>
                                  )}
                                </div>
                                <CardDescription className="flex items-center gap-2 text-sm">
                                  <Badge variant="outline" className="text-xs">
                                    {item.package.moment}
                                  </Badge>
                                  {item.package.eventName}
                                </CardDescription>
                                {item.customized && (
                                  <Badge variant="secondary" className="text-xs mt-1">
                                    Customized
                                  </Badge>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeFromCart(index)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </CardHeader>
                            <CardContent className="space-y-3">
                            {/* Musicians List */}
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <Users className="h-3 w-3 text-primary" />
                                <span>Musicians ({item.package.musicians.length})</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {item.package.musicians.map((musician, idx) => {
                                  const isRecommended = item.package.recommendedInstruments?.some(rec => 
                                    musician.toLowerCase().includes(rec.toLowerCase()) || 
                                    rec.toLowerCase().includes(musician.toLowerCase())
                                  );
                                  return (
                                    <Badge 
                                      key={idx} 
                                      variant={isRecommended ? "default" : "secondary"} 
                                      className={`text-xs ${isRecommended ? 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/50' : ''}`}
                                    >
                                      {musician}
                                      {isRecommended && <span className="ml-1">✨</span>}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Package Price */}
                            <div className="pt-2 border-t">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Package Price</span>
                                <div className="text-right">
                                  {item.package.adjustedPrice && item.package.adjustedPrice !== item.package.basePrice ? (
                                    <>
                                      <div className="text-lg font-bold text-primary">
                                        ${item.package.adjustedPrice.toLocaleString()}
                                      </div>
                                      <div className="text-xs text-muted-foreground line-through">
                                        ${item.package.basePrice.toLocaleString()}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-lg font-bold">
                                      ${item.package.basePrice.toLocaleString()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}

                      {/* Bundle Discount Alerts */}
                      {bundleDiscounts.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {bundleDiscounts.map((discount, idx) => (
                            <Card key={idx} className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800">
                              <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0">
                                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-lg px-3 py-1">
                                      🎉 {discount.discountPercentage}% OFF
                                    </Badge>
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-sm mb-1">
                                      {discount.bundleType === 'multi-moment' ? '🎭 Multi-Moment Bundle' : 
                                       discount.bundleType === 'consecutive' ? '📅 Multi-Event Bundle' : 
                                       '🎪 Special Bundle'}
                                    </h4>
                                    <p className="text-xs text-muted-foreground mb-2">
                                      {discount.description}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-bold text-green-600 dark:text-green-400">
                                        Save ${discount.savingsAmount.toLocaleString()}
                                      </span>
                                      <Badge variant="secondary" className="text-xs">
                                        Best Value
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}

                      {/* Dynamic Cart Incentives */}
                      {cart.length > 0 && (
                        <div className="mb-4">
                          <CartIncentives
                            packageCount={cart.length}
                            totalAmount={cart.reduce((sum, item) => sum + (item.package.adjustedPrice || item.package.basePrice), 0)}
                            eventCount={new Set(cart.map(item => item.package.eventIndex)).size}
                          />
                        </div>
                      )}

                      <Separator className="my-4" />

                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Subtotal ({cart.length} packages)</span>
                          <span>${cart.reduce((sum, item) => sum + (item.package.adjustedPrice || item.package.basePrice), 0).toLocaleString()}</span>
                        </div>
                        
                        {bundleDiscounts.length > 0 && (
                          <div className="flex justify-between text-sm text-green-600 dark:text-green-400 font-medium">
                            <span>Bundle Savings</span>
                            <span>-${bundleDiscounts.reduce((max, d) => Math.max(max, d.savingsAmount), 0).toLocaleString()}</span>
                          </div>
                        )}
                        
                        {getTotalSavings() > 0 && (
                          <div className="p-2 bg-green-50 dark:bg-green-950/20 rounded-lg">
                            <p className="text-xs text-green-700 dark:text-green-300 text-center font-medium">
                              🎉 Total Savings: ${getTotalSavings().toLocaleString()}
                            </p>
                          </div>
                        )}
                        
                        <Separator />
                        <div className="flex justify-between font-bold text-lg">
                          <span>Total</span>
                          <div className="text-right">
                            {getTotalSavings() > 0 && (
                              <div className="text-xs text-muted-foreground line-through font-normal">
                                ${cart.reduce((sum, item) => sum + (item.package.adjustedPrice || item.package.basePrice), 0).toLocaleString()}
                              </div>
                            )}
                            <span className="text-primary">${calculateTotal().toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Consultation Scheduler */}
                      <div className="mt-4">
                        <ConsultationScheduler 
                          onScheduleChange={setScheduledCall}
                          selectedSchedule={scheduledCall}
                        />
                      </div>

                      <Button 
                        className="w-full mt-6" 
                        size="lg"
                        onClick={handleCheckout}
                        disabled={!scheduledCall}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {scheduledCall ? "Book a Consultation" : "Select Call Time to Continue"}
                        {getTotalSavings() > 0 && scheduledCall && (
                          <Badge className="ml-2 bg-white text-primary">
                            Save ${getTotalSavings().toLocaleString()}
                          </Badge>
                        )}
                      </Button>
                      
                      {!scheduledCall && (
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          Please select a date and time for your discovery call above
                        </p>
                      )}
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Hero Section */}
        <div className="container max-w-7xl mx-auto px-4 py-8">
          {/* Trust Bar */}
          <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border border-primary/10 rounded-lg p-4 mb-8">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-medium">130+ Professional Musicians</span>
              </div>
              <div className="h-4 w-px bg-border hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="font-medium">1000+ Events Served</span>
              </div>
              <div className="h-4 w-px bg-border hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🏆</span>
                <span className="font-medium">Fully Insured & Licensed</span>
              </div>
              <div className="h-4 w-px bg-border hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">💯</span>
                <span className="font-medium">100% Money-Back Guarantee</span>
              </div>
            </div>
          </div>

          <div className="text-center mb-8 space-y-3">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <Badge variant="secondary" className="text-sm">
                AI-Matched To Your Taste
              </Badge>
            </div>
            <h2 className="text-3xl font-bold tracking-tight">
              Your Perfect Musicians, Ready To Book
            </h2>
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              Packages curated from your music preferences. One package per event. Price includes everything.
            </p>
          </div>

          {/* Event-Based Recommendations */}
          {Array.from(eventRecommendations.entries()).map(([eventIndex, packages]) => {
            // Cultural analysis is now processed server-side during submission only
            
            return (
              <div key={eventIndex} className="mb-16">
                {/* Event Header with Cultural Analysis */}
                <div className="mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold">
                          {packages[0]?.eventName || `Event ${eventIndex + 1}`}
                        </h3>
                        {(() => {
                          const selectedPackage = cart.find(item => item.package.eventIndex === eventIndex);
                          return selectedPackage ? (
                            <Badge className="bg-green-600 hover:bg-green-600">
                              ✓ Selected
                            </Badge>
                          ) : null;
                        })()}
                      </div>
                      <p className="text-muted-foreground">
                        {packages.length} ensemble package{packages.length === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>

                </div>

                {/* Package Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {packages.map((pkg, pkgIndex) => {
                    const eventHasPackage = cart.find(item => item.package.eventIndex === pkg.eventIndex);
                    const isThisPackageSelected = eventHasPackage?.package.name === pkg.name;
                    
                    return (
                      <Card 
                        key={`${eventIndex}-${pkgIndex}`}
                        className={`relative flex flex-col transition-all ${
                          isThisPackageSelected ? 'ring-2 ring-green-500 shadow-lg' : 
                          'hover:shadow-lg hover:scale-[1.02]'
                        }`}
                      >
                        {/* Badges */}
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                          {/* Best Value badge takes priority */}
                          {pkg.bestValue && (
                            <Badge className="bg-gradient-to-r from-emerald-500 to-green-500 text-white px-4 py-1 shadow-lg">
                              <DollarSign className="h-3 w-3 mr-1" />
                              Best Value
                            </Badge>
                          )}
                          {/* Perfect Match badge shows when not best value */}
                          {!pkg.bestValue && pkgIndex === 0 && (
                            <Badge className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 py-1 shadow-lg">
                              <Brain className="h-3 w-3 mr-1" />
                              Perfect Match
                            </Badge>
                          )}
                        </div>
                        
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline" className="text-xs">
                              {pkg.ensembleName?.toUpperCase() || 'ENSEMBLE'}
                            </Badge>
                            {!pkg.bestValue && pkg.tierBadge && (
                              <Badge variant="secondary" className="text-xs">
                                {pkg.tierBadge}
                              </Badge>
                            )}
                          </div>
                          <CardTitle className="text-lg">{pkg.name}</CardTitle>
                          <CardDescription className="text-sm line-clamp-2">{pkg.description}</CardDescription>
                        </CardHeader>
                        
                        <CardContent className="flex-1 space-y-4">
                          {/* Musicians Count */}
                          <div className="flex items-center gap-2 text-sm">
                            <Users className="h-4 w-4 text-primary" />
                            <span className="font-medium">{pkg.musicians.length} Professional Musicians</span>
                          </div>

                          {/* Key Features - Max 3 */}
                          <div className="space-y-1">
                            {pkg.features.slice(0, 3).map((feature, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1 flex-shrink-0" />
                                <span>{feature}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>

                        <CardFooter className="flex flex-col gap-3 pt-4 border-t">
                          {/* CTA Button */}
                          {eventHasPackage && !isThisPackageSelected && (
                            <div className="w-full p-2 bg-blue-50 dark:bg-blue-950/20 rounded text-center border border-blue-200 dark:border-blue-800">
                              <p className="text-xs font-medium text-blue-700 dark:text-blue-300">
                                Currently selected: "{eventHasPackage.package.name}"
                              </p>
                            </div>
                          )}
                          
                          <Button 
                            className="w-full h-12 text-base font-semibold"
                            onClick={() => addToCart(pkg)}
                            variant={isThisPackageSelected ? "secondary" : "default"}
                            size="lg"
                          >
                            {isThisPackageSelected ? (
                              <>✓ Selected for Event {eventIndex + 1}</>
                            ) : eventHasPackage ? (
                              <>Switch to This Package</>
                            ) : (
                              <>Select This Package</>
                            )}
                          </Button>
                        </CardFooter>
                      </Card>
                    );
                  })}
                </div>
                
                {/* Trust Footer */}
                <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                  <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <span>✓</span>
                      <span>Free Consultation</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>✓</span>
                      <span>Flexible Cancellation</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>✓</span>
                      <span>Professional Insurance</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>✓</span>
                      <span>Regional Manager Included</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {eventRecommendations.size === 0 && (
            <Card className="text-center py-12">
              <CardContent>
                <Music className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No recommendations found. Please complete your vibe builder.
                </p>
                <Button
                  onClick={() => navigate('/vibe-builder')}
                  className="mt-4"
                >
                  Build Your Profile
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Additional Services */}
        </div>
      </div>
    </>
  );
};

export default Shop;
